import { useContext, useEffect } from 'react';
import { VulnDrawerTabsContext } from '../NRVulnerabilitiesTable/NRVulnerabilityDetails/NRVulnerabilityDetails';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import NRTitleWithRightTotal from '../NRRightTotal/NRRightTotal';
import NRNoData from './NRNoData/NRNoData';
import NRVirtualizedScroll from '../NRVulnerabilitiesTable/NRVulnerabilityDetails/NRKnownExploits/KnownExploitInfo/KnownExploitInfoCard';
import NRHighlightedText from '../NRHighlightedText/NRHighlightedText';
import { CONSTANTS } from '../../constants/constants';
import NRValueTooltipIcon from '../NRReactGridCustomComponents/NRCustomProviders/NRValueTooltipIcon';
import { PACKAGE_MANAGER_ICONS } from '../NRReactGridCustomComponents/NRCustomProviders/NRSBOMTypeProvider';
import { useTheme } from '@mui/material/styles';
import NRLoader from '../NRLoader/NRLoader';
import Typography from '@mui/material/Typography';
const { BADGES } = CONSTANTS.TABLE.SBOM_TABLE.SBOM_DETAIL_DRAWER;

function NRDependencies({
  threshold,
  scrollTarget,
  index,
  scrollableId,
  context,
  dependencies,
  title,
  itemSize,
  showDivider = true,
  handleNameClick,
  isLoading
}) {
  const theme = useTheme();
  const { setScrollSpyTabsValue } = useContext(context || VulnDrawerTabsContext);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold,
    target: !!scrollTarget ? scrollTarget : undefined
  });

  useEffect(() => {
    setScrollSpyTabsValue(index, trigger);
  }, [trigger]);

  function NRDependency({ index, style }) {
    const theme = useTheme();
    const dependency = dependencies[index];
    const isDependency = dependency.direct !== undefined;
    const isDarkMode = theme.palette.mode === 'dark';
    const packageType = dependency.component?.package?.type;
    const iconDefinition = (!!packageType && PACKAGE_MANAGER_ICONS[packageType?.toUpperCase()]) || PACKAGE_MANAGER_ICONS.DEFAULT;
    const packageIcon = isDarkMode ? iconDefinition?.ICON_DARK || iconDefinition?.ICON : iconDefinition?.ICON;
    const tooltip = iconDefinition?.TOOLTIP;

    return (
      <Box sx={{ py: theme.spacing(0.5) }} style={style}>
        <Box sx={{ whiteSpace: 'nowrap', width: '100%', display: 'flex', alignItems: 'center' }}>
          <Typography
            component={'span'}
            sx={{
              textDecoration: 'none',
              cursor: !!handleNameClick ? 'pointer' : 'default',
              fontWeight: 470,
              fontSize: 14,
              paddingRight: theme.spacing(0.5),
              lineHeight: theme.spacing(3),
              ...(!!handleNameClick && { color: theme.palette.blue.link })
            }}
            {...(!!handleNameClick && { onClick: () => handleNameClick(dependency) })}
          >
            {dependency.component.name}
          </Typography>
          <Typography
            component={'span'}
            sx={{
              color: theme.palette.text.secondary,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              maxWidth: 470,
              paddingRight: theme.spacing(1),
              lineHeight: theme.spacing(3)
            }}
          >
            {dependency.component.version?.id}
          </Typography>
          {!!dependency.component?.package?.type && (
            <NRValueTooltipIcon
              style={{ marginRight: 8 }}
              icon={packageIcon}
              tooltip={tooltip}
              tooltipComponent={
                <span>
                  Package Match: <b>{tooltip}</b>
                </span>
              }
            />
          )}
          {dependency?.badges?.map(badgeKey => (
            <NRHighlightedText
              fontSize={12}
              lineHeight={14}
              style={{ marginRight: 8 }}
              text={BADGES[badgeKey].LABEL}
              color={theme.palette.text.primary}
              alphaValue={0.1}
              textAlphaValue={0.6}
              tooltipContent={<span style={{ whiteSpace: 'pre-line' }}>{BADGES[badgeKey].TOOLTIP}</span>}
            />
          ))}
        </Box>
        {isDependency && (
          <Box sx={{ whiteSpace: 'nowrap', width: '100%', display: 'flex', alignItems: 'center' }}>
            <Box
              component={'span'}
              sx={{
                color: theme.palette.text.secondary,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: 470,
                paddingRight: theme.spacing(1),
                lineHeight: theme.spacing(3)
              }}
            >
              {dependency.direct ? 'Direct' : 'Indirect'}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box id={scrollableId} mt={theme.spacing(3)}>
      <NRTitleWithRightTotal totalCount={dependencies?.length || 0} title={title} />
      {isLoading ? (
        <Box sx={{ height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <NRLoader />
        </Box>
      ) : (
        <>
          {!!dependencies?.length && <NRVirtualizedScroll list={dependencies} Component={NRDependency} itemHeight={itemSize} />}
          {!dependencies?.length && <NRNoData noDataTitle={title} />}
        </>
      )}
      {showDivider && <Divider sx={{ pt: theme.spacing(2) }} />}
    </Box>
  );
}

export default NRDependencies;
